import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { isXlsxBuffer, decodeText } from '../DocumentViewer/spreadsheetBytes';
import { FortuneCell, FortuneCellData, FortuneSheet } from './types';

const MIN_ROWS = 50;
const MIN_COLS = 26;

// Returns the SSF format string for a cell, or 'General' when absent/generic.
const numFmt = (z?: string | number): string => (typeof z === 'string' && z !== 'General' ? z : 'General');

/** Convert one SheetJS worksheet into Fortune-Sheet celldata, preserving formulas. Exported for unit tests. */
export const worksheetToCelldata = (
    ws: XLSX.WorkSheet,
): { celldata: FortuneCellData[]; rows: number; cols: number; config: FortuneSheet['config'] } => {
    const celldata: FortuneCellData[] = [];
    const ref = ws['!ref'];
    if (!ref) return { celldata, rows: MIN_ROWS, cols: MIN_COLS, config: {} };

    const range = XLSX.utils.decode_range(ref);
    // Iterate only the cells that actually exist — looping the full !ref
    // rectangle explodes to millions of iterations on sparse/templated sheets.
    Object.keys(ws).forEach((addr) => {
        if (addr.startsWith('!')) return;
        const cell = ws[addr] as XLSX.CellObject;
        const { r, c } = XLSX.utils.decode_cell(addr);

        const fcell: FortuneCell = {};
        // SheetJS stores formulas without the leading '='; Fortune-Sheet wants it.
        if (cell.f) fcell.f = `=${cell.f}`;

        if (cell.t === 'n') {
            fcell.v = cell.v as number;
            fcell.m = cell.w ?? String(cell.v);
            fcell.ct = { t: 'n', fa: numFmt(cell.z) };
        } else if (cell.t === 'b') {
            fcell.v = cell.v as boolean;
            fcell.m = cell.w ?? (cell.v ? 'TRUE' : 'FALSE');
            fcell.ct = { t: 'b', fa: 'General' };
        } else if (cell.t === 'e') {
            fcell.v = (cell.w as string) ?? String(cell.v);
            fcell.m = fcell.v as string;
            fcell.ct = { t: 'e', fa: 'General' };
        } else if (cell.v !== undefined && cell.v !== null) {
            fcell.v = cell.v == null ? '' : String(cell.v);
            fcell.m = cell.w ?? String(fcell.v);
            fcell.ct = { t: 's', fa: '@' };
        }

        if (fcell.v === undefined && !fcell.f) return;
        celldata.push({ r, c, v: fcell });
    });

    const config: NonNullable<FortuneSheet['config']> = {};
    const merges = ws['!merges'];
    if (merges?.length) {
        config.merge = {};
        merges.forEach((m) => {
            config.merge![`${m.s.r}_${m.s.c}`] = { r: m.s.r, c: m.s.c, rs: m.e.r - m.s.r + 1, cs: m.e.c - m.s.c + 1 };
        });
    }
    const cols = ws['!cols'];
    if (cols?.length) {
        config.columnlen = {};
        cols.forEach((col, i) => {
            if (!col) return;
            const px = col.wpx ?? (col.wch != null ? Math.round(col.wch * 7 + 5) : undefined);
            if (px != null) config.columnlen![String(i)] = px;
        });
    }
    const rowsCfg = ws['!rows'];
    if (rowsCfg?.length) {
        config.rowlen = {};
        rowsCfg.forEach((row, i) => {
            if (!row) return;
            const px = row.hpx ?? (row.hpt != null ? Math.round((row.hpt * 96) / 72) : undefined);
            if (px != null) config.rowlen![String(i)] = px;
        });
    }

    return {
        celldata,
        rows: Math.max(MIN_ROWS, range.e.r + 1),
        cols: Math.max(MIN_COLS, range.e.c + 1),
        config,
    };
};

const buildSheet = (
    name: string,
    index: number,
    celldata: FortuneCellData[],
    rows: number,
    cols: number,
    config?: FortuneSheet['config'],
): FortuneSheet => ({
    name,
    id: String(index),
    order: index,
    status: index === 0 ? 1 : 0,
    celldata,
    row: rows,
    column: cols,
    ...(config && Object.keys(config).length ? { config } : {}),
});

const parseXlsx = (buffer: ArrayBuffer): FortuneSheet[] => {
    const wb = XLSX.read(buffer, { type: 'array', cellFormula: true, cellNF: true, cellStyles: true });
    return wb.SheetNames.map((name, i) => {
        const { celldata, rows, cols, config } = worksheetToCelldata(wb.Sheets[name]);
        return buildSheet(name, i, celldata, rows, cols, config);
    });
};

const parseCsv = (text: string): FortuneSheet[] => {
    // Capture raw CSV tokens via PapaParse BEFORE SheetJS numeric coercion.
    // XLSX.read converts 17+ digit values to rounded JS floats, making cell.w
    // unreliable for recovering the original token; the raw PapaParse strings are exact.
    const rawByAddr = new Map<string, string>();
    Papa.parse<string[]>(text, { skipEmptyLines: false }).data.forEach((row, r) => {
        row.forEach((rawVal, c) => {
            if (rawVal !== '' && rawVal != null) {
                rawByAddr.set(XLSX.utils.encode_cell({ r, c }), rawVal);
            }
        });
    });

    // Parse CSV with the same engine as xlsx so numbers, dates and formats are
    // detected identically. `decodeText` (caller) already handled UTF-16/BOM.
    const wb = XLSX.read(text, { type: 'string', cellNF: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (ws) {
        // Keep long numeric IDs (>=16 digits, e.g. account/UTR numbers) as text —
        // a JS double can't represent them exactly, so typing them as numbers corrupts them.
        Object.keys(ws).forEach((addr) => {
            if (addr.startsWith('!')) return;
            const cell = ws[addr] as XLSX.CellObject;
            const raw = rawByAddr.get(addr) ?? '';
            const digits = raw.replace(/[,\s]/g, '');
            if (cell.t === 'n' && /^\d{16,}$/.test(digits)) {
                cell.t = 's';
                cell.v = raw;
                delete cell.z;
            }
        });
    }
    const { celldata, rows, cols, config } = ws
        ? worksheetToCelldata(ws)
        : { celldata: [], rows: MIN_ROWS, cols: MIN_COLS, config: {} as FortuneSheet['config'] };
    return [buildSheet('Sheet1', 0, celldata, rows, cols, config)];
};

/**
 * Parse fetched file bytes into Fortune-Sheet sheets. Detects XLSX by content
 * (ZIP magic bytes) so a mislabelled .csv that is really an .xlsx still opens,
 * mirroring DocumentViewer's detection. When the MIME type doesn't confirm CSV
 * (empty/wrong — common for local file pickers on some OS/browsers), fall back
 * to the file's extension before defaulting to XLSX, so a real local .csv with
 * a blank/incorrect `type` doesn't get mis-parsed as a workbook.
 */
export const fileBytesToFortuneSheets = (buffer: ArrayBuffer, mimeType?: string, fileName?: string): FortuneSheet[] => {
    if (isXlsxBuffer(buffer)) return parseXlsx(buffer);
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (mimeType === 'text/csv' || mimeType === 'application/csv' || ext === 'csv') return parseCsv(decodeText(buffer));
    return parseXlsx(buffer);
};

/**
 * Read a File object into FortuneSheet[]. Use this in the import flow
 * (xlsx/xls/csv) to go from a user-selected file to sheets ready for
 * SheetEditorDrawer's `sheets` prop.
 */
export const importFileToSheets = async (file: File): Promise<FortuneSheet[]> => {
    const buffer = await file.arrayBuffer();
    return fileBytesToFortuneSheets(buffer, file.type, file.name);
};

/** Normalise a Fortune-Sheet sheet (celldata or 2D data) to a flat cell list. */
const getCells = (sheet: FortuneSheet): FortuneCellData[] => {
    if (sheet.celldata && sheet.celldata.length) return sheet.celldata;
    if (sheet.data) {
        const out: FortuneCellData[] = [];
        sheet.data.forEach((row, r) => {
            row?.forEach((cell, c) => {
                if (cell == null) return;
                out.push({ r, c, v: cell });
            });
        });
        return out;
    }
    return [];
};

/**
 * Normalise a sheet to Fortune-Sheet's `celldata` init form. `<Workbook data>`
 * builds the grid from `celldata`, but `getAllSheets()` (what save persists)
 * returns only the runtime 2D `data` plus selection state — so re-feeding a
 * saved sheet to the editor renders an empty grid. Rebuild `celldata` from
 * `data`, drop the runtime-only `data`/`luckysheet_select_save`, and keep
 * everything else (`config` merges/widths/styles, `frozen`, …). No-op for
 * sheets that already carry `celldata` (the import path).
 */
export const toCelldataSheet = (sheet: FortuneSheet): FortuneSheet => {
    const { data, luckysheet_select_save, ...rest } = sheet;
    return { ...rest, celldata: getCells(sheet) };
};

const cellRaw = (v: FortuneCellData['v']): { value: string | number | boolean | null; formula?: string } => {
    if (v == null) return { value: null };
    if (typeof v === 'object') return { value: v.v ?? null, formula: v.f };
    return { value: v };
};

const fortuneSheetToWorksheet = (sheet: FortuneSheet): XLSX.WorkSheet => {
    const ws: XLSX.WorkSheet = {};
    let maxR = 0;
    let maxC = 0;
    getCells(sheet).forEach(({ r, c, v }) => {
        const { value, formula } = cellRaw(v);
        if (value == null && !formula) return;
        maxR = Math.max(maxR, r);
        maxC = Math.max(maxC, c);

        const out: XLSX.CellObject = {} as XLSX.CellObject;
        if (formula) out.f = formula.replace(/^=/, '');
        if (typeof value === 'number') {
            out.t = 'n';
            out.v = value;
        } else if (typeof value === 'boolean') {
            out.t = 'b';
            out.v = value;
        } else if (value != null) {
            out.t = 's';
            out.v = String(value);
        } else {
            // Formula cell with no cached value.
            out.t = 'n';
            out.v = 0;
        }
        // Carry the SSF number format through to the exported workbook.
        const ct = v && typeof v === 'object' ? (v as FortuneCell).ct : undefined;
        if (out.t === 'n' && ct?.fa && ct.fa !== 'General') out.z = ct.fa;
        ws[XLSX.utils.encode_cell({ r, c })] = out;
    });
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });
    if (sheet.config?.merge) {
        ws['!merges'] = Object.values(sheet.config.merge).map((m) => ({
            s: { r: m.r, c: m.c },
            e: { r: m.r + m.rs - 1, c: m.c + m.cs - 1 },
        }));
    }
    if (sheet.config?.columnlen) {
        const outCols: XLSX.ColInfo[] = [];
        Object.entries(sheet.config.columnlen).forEach(([i, w]) => {
            outCols[Number(i)] = { wpx: w };
        });
        ws['!cols'] = outCols;
    }
    if (sheet.config?.rowlen) {
        const outRows: XLSX.RowInfo[] = [];
        Object.entries(sheet.config.rowlen).forEach(([i, h]) => {
            outRows[Number(i)] = { hpx: h };
        });
        ws['!rows'] = outRows;
    }
    return ws;
};

// Excel forbids these characters in sheet names and caps length at 31.
const sanitizeSheetName = (name: string, index: number): string => {
    const cleaned = (name || `Sheet${index + 1}`)
        .replace(/[\\/?*[\]:]/g, ' ')
        .slice(0, 31)
        .trim();
    return cleaned || `Sheet${index + 1}`;
};

// Excel rejects duplicate sheet names (case-insensitive); suffix collisions
// while keeping within the 31-char cap so book_append_sheet never throws.
const uniqueSheetName = (base: string, used: Set<string>): string => {
    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
        const suffix = ` (${n})`;
        name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
        n += 1;
    }
    used.add(name.toLowerCase());
    return name;
};

const triggerDownload = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const stripExtension = (fileName: string): string => fileName.replace(/\.(xlsx|xls|csv)$/i, '');

// Prevent CSV formula injection: a cell beginning with = + - @ can execute when
// the exported file is opened in Excel/Sheets. Prefix such strings with a quote.
const sanitizeCsvCell = (value: string | number | boolean): string | number | boolean =>
    typeof value === 'string' && /^[=+\-@]/.test(value) ? `'${value}` : value;

/** Export every sheet to a single .xlsx workbook and download it. */
export const downloadAsXlsx = (sheets: FortuneSheet[], fileName: string): void => {
    const wb = XLSX.utils.book_new();
    const usedNames = new Set<string>();
    sheets.forEach((sheet, i) => {
        const name = uniqueSheetName(sanitizeSheetName(sheet.name, i), usedNames);
        XLSX.utils.book_append_sheet(wb, fortuneSheetToWorksheet(sheet), name);
    });
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
    triggerDownload(
        new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `${stripExtension(fileName)}.xlsx`,
    );
};

/** Export a single sheet (the active one) to .csv and download it. */
export const downloadAsCsv = (sheet: FortuneSheet, fileName: string): void => {
    const cells = getCells(sheet);
    let maxR = 0;
    let maxC = 0;
    cells.forEach(({ r, c }) => {
        maxR = Math.max(maxR, r);
        maxC = Math.max(maxC, c);
    });
    const aoa: (string | number | boolean)[][] = Array.from({ length: maxR + 1 }, () => Array.from({ length: maxC + 1 }, () => ''));
    cells.forEach(({ r, c, v }) => {
        // Use the raw value, not the display string `m` — for number-formatted
        // cells `m` carries separators/currency (e.g. "1,234") which corrupts CSV.
        const { value } = cellRaw(v);
        aoa[r][c] = sanitizeCsvCell((value ?? '') as string | number | boolean);
    });
    const csv = Papa.unparse(aoa);
    triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${stripExtension(fileName)}.csv`);
};
